import makeEconomy from "./game/economy";
import makeGrid from "./geometry/grid";
import initReceiver from "./visual/receiver";
import makeRenderer from "./visual/renderer";
import makeWorld from "./world/world";
import makeAdvancements from './world/advancements';
import makeKnownAdvancements from "./game/knownadvancements";
import makeTicker from "./game/ticker";
import makeUniqueResources from "./world/uniqueresources";
import makeHints from "./game/hints";
import makeLogbook from "./game/logbook";

const makeGame = (canvas, gridWidth, gridHeight, gridSide, stateUpdateCallback, setDialogState, setGameState) => {
    const grid = makeGrid(gridWidth, gridHeight, gridSide);
    const world = makeWorld(grid, gridWidth, gridHeight);
    const economy = makeEconomy(gridWidth * gridHeight);
    const advancements = makeAdvancements();
    const uniqueResources = makeUniqueResources();
    const knownAdvancements = makeKnownAdvancements(advancements.getAllWithFulfilledRequirements([], []).map(a => a.advancement.id));
    const hints = makeHints();
    const logbook = makeLogbook();

    economy.setLandPoints(2, 0, 0);

    const getAvailableAdvancements = (u, v) => {
        const landType = world.getTerrain(u, v);
        const uniqueResource = world.getUniqueResource(u, v);
        const advancementLevel = (advancements.getAdvancement(world.getAdvancement(u, v)) || { level: 0 }).level
            || uniqueResource && 2 || 0;
        const affordable = economy.getAdvancementPoints() > advancementLevel;
        return advancements
            .getAvailableAdvancements(landType, world.getAdvancement(u, v), uniqueResource)
            .filter(a => knownAdvancements.isKnown(a.id))
            .map(a => ({ ...a, affordable: affordable, cost: advancementLevel + 1 }));
    };

    const judgeCell = (u, v, callbacks) => {
        if (world.isClaimed(u, v)) {
            const availableAdvancements = getAvailableAdvancements(u, v);
            if (availableAdvancements.length > 0 && availableAdvancements.every(a => a.affordable)) {
                callbacks.advancementPossible && callbacks.advancementPossible(availableAdvancements);
            } else {
                callbacks.claimedButAdvancementNotPossible && callbacks.claimedButAdvancementNotPossible(availableAdvancements);
            }
        } else {
            const adjacent = grid.getStrictlyAdjacent(u, v);
            if (economy.getLandPoints() > 0) {
                if (world.getClaimedCount() === 0 || adjacent.some(uv => world.isClaimed(uv[0], uv[1]))) {
                    callbacks.claimPossible && callbacks.claimPossible();
                }
            }
        }
    };

    const renderer = makeRenderer(grid, canvas, {
        getTerrain: (u, v) => world.getTerrain(u, v),
        getUniqueResource: (u, v) => uniqueResources.getResource(world.getUniqueResource(u, v)),
        getAdvancement: (u, v) => advancements.getAdvancement(world.getAdvancement(u, v)),
        isClaimed: (u, v) => world.isClaimed(u, v),
        canBeAdvanced: (u, v) => getAvailableAdvancements(u, v).length > 0,
        judgeCell: judgeCell
    });
    window.addEventListener('resize', () => renderer.render());

    let hoverUV = [0, 0];

    const signalGameStateUpdate = () => {
        const landType = world.getTerrain(hoverUV[0], hoverUV[1]);
        let landUse = "Unclaimed";
        let combinationsFound = 0;
        let combinationsTotal = 0;
        let descendantsFound = 0;
        let descendantsTotal = 0;
        if (world.isClaimed(hoverUV[0], hoverUV[1])) {
            landUse = "Unexploited";
            const advancement = world.getAdvancement(hoverUV[0], hoverUV[1]);
            const resource = world.getUniqueResource(hoverUV[0], hoverUV[1]);
            if (advancement) {
                landUse = advancements.getAdvancement(advancement).name;
                const combos = advancements.getAllAdvancementsRequiring(advancement);
                combinationsTotal = combos.length;
                combinationsFound = combos.filter(a => knownAdvancements.isKnown(a.id)).length;
                const descendants = advancements.getAllAdvancementsDescendingFrom(advancement);
                descendantsTotal = descendants.length;
                descendantsFound = descendants.filter(a => knownAdvancements.isKnown(a.id)).length;
            } else if (resource) {
                landUse = uniqueResources.getResource(resource).name;
            }
        }
        setGameState({
            landPoints: economy.getLandPoints(),
            maxLandPoints: economy.getMaxLandPoints(),
            advancementPoints: economy.getAdvancementPoints(),
            maxAdvancementPoints: economy.getMaxAdvancementPoints(),
            claimedCount: world.getClaimedCount(),
            untouchedCount: world.getUntouchedClaimedCount(),
            allAdvancements: advancements.getAll().length,
            knownAdvancements: knownAdvancements.knownCount(),
            usedAdvancements: knownAdvancements.usedCount(),
            landType: landType,
            landUse: landUse,
            u: hoverUV[0],
            v: hoverUV[1],
            combinationsFound: combinationsFound,
            combinationsTotal: combinationsTotal,
            descendantsFound: descendantsFound,
            descendantsTotal: descendantsTotal,
            hintBuyCost: hints.getNextHintCost(),
            hintAvailable: hints.shouldCreateHint() && hints.peekHint(
                advancements.getAllWithFulfilledRequirements(knownAdvancements.allKnown(), []).map(a => a.advancement),
                id => knownAdvancements.isKnown(id),
                3) !== undefined,
            hintAffordable: economy.getAdvancementPoints() >= hints.getNextHintCost()
        });
        stateUpdateCallback();
    };

    const signalDialogStateUpdate = (state) => {
        setDialogState(state || {});
        stateUpdateCallback();
    };

    const showHint = () => {
        const hint = hints.createHint(
            advancements.getAllWithFulfilledRequirements(knownAdvancements.allKnown(), []).map(a => a.advancement),
            id => knownAdvancements.isKnown(id),
            3);
        if (hint) {
            logbook.addHint(hint.requirementIds.map(r => advancements.getAdvancement(r)), advancements.getAdvancement(hint.advancementId));
            signalDialogStateUpdate({ hint: hint });
        } else {
            signalDialogStateUpdate();
        }
    };

    signalGameStateUpdate();

    const ticker = makeTicker(economy, {
        addAdvancementPoint: () => {
            economy.gainAdvancementPoint();
            renderer.render();
            signalGameStateUpdate();
        }
    });

    initReceiver(grid, canvas, () => renderer.getTransform(), {
        click: (u, v) => {
            judgeCell(u, v, {
                advancementPossible: availableAdvancements => {
                    const currentAdvancement = advancements.getAdvancement(world.getAdvancement(u, v));
                    signalDialogStateUpdate({ advancementOptions: availableAdvancements, advancementRemoveCost: !currentAdvancement ? 0 : currentAdvancement.level*(currentAdvancement.level+1)/2, advancementUV: [u, v] });
                },

                claimedButAdvancementNotPossible: availableAdvancements => {
                    const currentAdvancement = advancements.getAdvancement(world.getAdvancement(u, v));
                    if (availableAdvancements.length > 0 || currentAdvancement) {
                        signalDialogStateUpdate({ advancementOptions: availableAdvancements, advancementRemoveCost: !currentAdvancement ? 0 : currentAdvancement.level*(currentAdvancement.level+1)/2, advancementUV: [u, v] });
                    }
                },
    
                claimPossible: () => {
                    world.claim(u, v);
                    economy.updateMaxAdvancementPoints(world.getUntouchedClaimedCount());
                    if (world.getUniqueResource(u, v)) {
                        for (let a of advancements.getAdvancementsForResource(world.getUniqueResource(u, v))) {
                            knownAdvancements.learn(a.id);
                        }
                        signalDialogStateUpdate({ newUniqueResource: uniqueResources.getResource(world.getUniqueResource(u, v)) });
                    } else {
                        economy.useLandPoints(1);
                    }
                    signalGameStateUpdate();
                    renderer.setFocus(u, v, true);
                }
            });
            renderer.render();
        },

        hover: (u, v) => {
            hoverUV = [u, v];
            renderer.setFocus(u, v);
            signalGameStateUpdate();
            renderer.render();
        }
    });

    renderer.render();

    return {
        selectAdvancement: (advancementId, uv) => {
            if (!advancementId) {
                signalDialogStateUpdate();
                return;
            }
            const advancementLevel = advancements.getAdvancement(advancementId).level;
            world.advance(uv[0], uv[1], advancementId);
            economy.useAdvancementPoints(advancementLevel);
            economy.setLandPoints(world.getAdvancementClaimedCount(), world.getUntouchedClaimedCount(), world.getUntouchedResourceClaimedCount());
            economy.updateMaxAdvancementPoints(world.getUntouchedClaimedCount());

            renderer.setFocus(uv[0], uv[1], true);
            renderer.render();

            knownAdvancements.use(advancementId);

            const newKnown = [];
            const adjacent = grid.getStrictlyAdjacent(uv[0], uv[1]);
            for (let i1 = 0, i2 = 1; i1 < adjacent.length; i1++, i2 = (i1 + 1) % adjacent.length) {
                const neighbourAdvancements = [world.getAdvancement(adjacent[i1][0], adjacent[i1][1]), world.getAdvancement(adjacent[i2][0], adjacent[i2][1])];
                const matching = advancements.getAllWithFulfilledRequirements([advancementId, ...neighbourAdvancements], []);
                const unknownMatch = matching.filter(m => !knownAdvancements.isKnown(m.advancement.id));
                newKnown.push.apply(newKnown, unknownMatch);
                for (let toLearn of unknownMatch) {
                    knownAdvancements.learn(toLearn.advancement.id);
                }
            }

            for (let discovery of newKnown) {
                logbook.addDiscovery(advancements.getAdvancement(discovery.advancement.ancestor) || discovery.advancement.land, discovery.advancement);
            }

            if (newKnown.length > 0) {
                const news = newKnown.map(m => ({
                    ...m.advancement,
                    requiredAdvancements: m.match.map(r => advancements.getAdvancement(r)),
                    ancestorAdvancement: m.advancement.ancestor ? advancements.getAdvancement(m.advancement.ancestor) : undefined
                }));
                hints.builtAdvancementWithCombo();
                signalDialogStateUpdate({ newAdvancementsNews: news });
            }
            else {
                hints.builtAdvancementWithoutCombo();
                signalDialogStateUpdate();
            }
            signalGameStateUpdate();
        },

        removeAdvancements: (cost, uv) => {
            world.removeAdvancements(uv[0], uv[1]);
            economy.useAdvancementPoints(cost);
            economy.setLandPoints(world.getAdvancementClaimedCount(), world.getUntouchedClaimedCount(), world.getUntouchedResourceClaimedCount());
            economy.updateMaxAdvancementPoints(world.getUntouchedClaimedCount());
            renderer.setFocus(uv[0], uv[1], true);
            renderer.render();
            signalDialogStateUpdate();
            signalGameStateUpdate();
        },

        tick: () => ticker.tick(),

        closeNewAdvancements: () => {
            signalDialogStateUpdate();
            renderer.render();
        },

        closeHint: () => {
            signalDialogStateUpdate();
            renderer.render();
        },

        closeNewUniqueResource: () => {
            signalDialogStateUpdate();
            renderer.render();
        },

        openLogBook: () => {
            signalDialogStateUpdate({ logBook: logbook.getEntries() });
        },

        closeLogBook: () => {
            signalDialogStateUpdate();
            renderer.render();
        },

        buyHint: () => {
            const potentialHint = hints.peekHint(
                advancements.getAllWithFulfilledRequirements(knownAdvancements.allKnown(), []).map(a => a.advancement),
                id => knownAdvancements.isKnown(id),
                3);
            if (economy.getAdvancementPoints() >= hints.getNextHintCost() && potentialHint) {
                economy.useAdvancementPoints(hints.getNextHintCost());
                showHint();
                signalGameStateUpdate();
            }
        }
    };
}

export default makeGame;
